import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { createDeletion, loadEmployeeUser, uploadToModule } from "./api";
import { DOC_TYPE_DEATH_CERTIFICATE } from "./constants";
import { employeeProfile, formatDob, getFamilyMembers, getSessionUser, isSupportedDoc, phoneOk, type UploadedDoc } from "./helpers";
import { ActionBar, DocUploader, Field, NoteBox, PageShell, Section, SubtitleBanner, TextArea, TextInput } from "./ui";
import { BRAND } from "./constants";

type Dep = { id: string; firstName: string; lastName: string; relation: string; dob: string; startDate: string };

const DeletionParentsForm = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(getSessionUser());
  const profile = employeeProfile(user);
  const [contactNo, setContactNo] = useState(profile.contactNo);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [docs, setDocs] = useState<UploadedDoc[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEmployeeUser().then((u) => {
      setUser(u);
      const p = employeeProfile(u);
      setContactNo((prev) => prev || p.contactNo);
    });
  }, []);

  const dependents: Dep[] = useMemo(() => {
    return getFamilyMembers(user)
      .filter((m: any) => ["father", "mother"].includes(String(m?.relation ?? "").toLowerCase()))
      .map((m: any, idx: number) => ({
        id: String(m?.id ?? idx),
        firstName: String(m?.firstname ?? ""),
        lastName: String(m?.lastname ?? ""),
        relation: String(m?.relation ?? ""),
        dob: formatDob(m?.birthDate),
        startDate: formatDob(m?.startDate),
      }));
  }, [user]);

  const selected = dependents.find((d) => d.id === selectedId) ?? null;

  const handleSave = async () => {
    const e: Record<string, string> = {};
    const missing: string[] = [];
    if (!contactNo.trim()) missing.push((e.contactNo = "Contact No is required"));
    else if (!phoneOk(contactNo)) missing.push((e.contactNo = "Invalid phone number (10-15 digits, optional +)"));
    if (!selectedId) missing.push("Please select a dependent to delete");
    if (!reason.trim()) missing.push((e.reason = "Reason for deletion is required"));
    setErrors(e);
    if (missing.length) {
      toast.error(missing.join("\n"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        Action: "C",
        EmpID: profile.employeeNo,
        RelativeFirstName: selected?.firstName ?? "",
        RelativeLastName: selected?.lastName ?? "",
        Relationship: selected?.relation ?? "",
        ToDocuments: {
          results: docs.map((d) => ({
            FileName: d.fileName,
            FileExtension: d.fileExtension,
            TypeOfDocument: DOC_TYPE_DEATH_CERTIFICATE,
            Content: d.key,
          })),
        },
      };
      const res = await createDeletion(payload);
      if (res?.success) {
        toast.success(res?.message || "Request Submitted Successfully.");
        navigate(all_routes.idCard.deletionOfDependent);
      } else toast.error(res?.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="Inclusion and Deletion">
      <SubtitleBanner text="Application for Deletion of dependent parents including widow mother" />
      <Section title="Employee Details">
        <Field label="Employee No.">
          <TextInput value={profile.employeeNo} disabled readOnly />
        </Field>
        <Field label="Employee Name">
          <TextInput value={profile.employeeName} disabled readOnly />
        </Field>
        <Field label="Designation">
          <TextInput value={profile.designation} disabled readOnly />
        </Field>
        <Field label="Pay Scale">
          <TextInput value={profile.payScale} disabled readOnly />
        </Field>
        <Field label="Contact No." required error={errors.contactNo}>
          <TextInput value={contactNo} placeholder="Enter Contact Number" onChange={(e) => setContactNo(e.target.value)} />
        </Field>
      </Section>

      <Section title="Dependent Details">
        {dependents.length === 0 && <div style={{ color: "#8A93A8" }}>No records found</div>}
        <div className="df-pick-grid">
        {dependents.map((dep) => {
          const active = selectedId === dep.id;
          return (
            <button
              key={dep.id}
              type="button"
              className="w-100 text-start mb-2"
              onClick={() => setSelectedId(dep.id)}
              style={{
                background: active ? "#fff6f6" : "#fff",
                border: `1px solid ${active ? BRAND : "#E2E8F0"}`,
                borderRadius: 8,
                padding: 12,
              }}
            >
              <div className="fw-bold">
                {dep.firstName} {dep.lastName}
              </div>
              <div style={{ color: "#5B6270" }}>
                {dep.relation} · Date of Birth: {dep.dob}
              </div>
              <div style={{ color: "#5B6270" }}>Start Date of dependency: {dep.startDate}</div>
            </button>
          );
        })}
        </div>
      </Section>

      <Section title="Reason for deletion">
        <Field label="Reason for deletion" required error={errors.reason}>
          <TextArea value={reason} placeholder="Enter reason" onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Section>

      <NoteBox
        title="Note"
        text="If reason of deletion is increase in income then please submit separate request of deletion as dependent for other parent as well."
      />

      <DocUploader
        title="Upload Required Documents"
        hint="PDF, JPG, PNG supported"
        docs={docs}
        onAdd={async (file) => {
          if (!isSupportedDoc(file)) {
            toast.error("Only PDF, JPG or PNG files are allowed");
            return;
          }
          const res = await uploadToModule(file, "sap_dependent_deletion", file.type || "application/octet-stream", { fileName: file.name });
          if (res.ok) setDocs((prev) => [...prev, { fileName: file.name, fileExtension: file.type || "application/octet-stream", fileSize: file.size, key: res.key }]);
          else toast.error(res.message || "Something went wrong");
        }}
        onRemove={(i) => setDocs((prev) => prev.filter((_, idx) => idx !== i))}
      />
      <div className="mb-3" style={{ color: "#8A93A8", fontSize: 13 }}>
        (Upload Death Certificate in case of death of parent)
      </div>

      <ActionBar
        left="Cancel"
        right="Delete Dependency"
        busy={saving}
        onLeft={() => navigate(all_routes.idCard.deletionOfDependent)}
        onRight={handleSave}
      />
    </PageShell>
  );
};

export default DeletionParentsForm;
